/**
 * The graph window: the architecture drawn on a canvas, ported from the prototype.
 *
 * - Drag the background to pan (or hold the middle button anywhere, over a node too), the wheel
 *   or a pinch to zoom, a node to move it.
 * - Click a node or a link to select it (the inspector shows it).
 * - Groups are circles around their nodes: click a group's rim or name to select it, drag it
 *   to move all its nodes.
 * - Links between groups are bundled into one thick link with a count; click it to see its
 *   links (the inspector lists them). Selecting a group or a node opens the bundles it is in.
 * - Drag from the handle beside a node (or with the link tool, or Shift) to another node to
 *   link them; a menu asks which kind of link, suggesting the likely one.
 * - On a selected link, drag one of its round ends onto another node to reconnect it.
 *   Alt+click a link to delete it.
 * - Right-click anything for what can be done with it (`menu.ts`); it gets selected too.
 * - The 2D/3D button (or `3`) spreads the graph in depth: drag the background to turn it,
 *   Ctrl+drag to pan. Everything else works the same way.
 * - Files of the computer dropped on it are kept as the project's assets, and the Add node
 *   window opens where they were dropped for a node attached to them. Whatever the view, what
 *   is dropped or added goes where the pointer is (`spot.ts`), and Ctrl+C / Ctrl+X / Ctrl+V
 *   copy, cut and paste the selected nodes with their links (`paste.ts`, `state/clipboard.ts`).
 * - The class diagram button (or `D`) shows the same graph as UML boxes in layers
 *   (`diagram/`); the nodal view stays mounted underneath, so switching back finds it as it was.
 * - The board (`board/`, Miro's) shows every node as an item placed freely, in frames.
 *
 * This file only puts the pieces together. Each one lives next to it:
 * `pointer.ts` (gestures), `canvas.ts` (size, colours, animation), `picture.ts` (what to draw),
 * `renderer.ts` (who draws: the graphics card with `gl/`, or the processor with `paint2d.ts`),
 * `edits.ts` (what a gesture changes in the core), and the overlays `LinkMenu`, `ZoomBar`,
 * `SelectionPill`, `PeopleLayer` (the others' cursors and selections), and `diagram/DiagramView`. The scene itself is a service (`state/scene/`), kept in step with the
 * core by `state/sceneSync.ts`, so this window holds no state worth keeping.
 */
import { Show, createEffect, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { pointerCursor } from "../../state/cursors";
import { foldedGroupOf, type Focus } from "../../state/scene";
import { drawsBoxes, selectedNodeIds } from "../../state/view";
import { toast } from "../../shared/toast";
import { assetStem, pictureOf } from "../../shared/assets";
import { createPictureCache } from "./images";
import { startCanvas, type CanvasControl } from "./canvas";
import { createLink, reconnectLink } from "./edits";
import { LinkMenu, type LinkMenuState } from "./LinkMenu";
import { graphMenu } from "./menu";
import { DiagramView } from "./diagram/DiagramView";
import { BoardView } from "./board/BoardView";
import { DocumentView } from "./document/DocumentView";
import { createPointer, type Point, type Target } from "./pointer";
import { PeopleLayer } from "./PeopleLayer";
import { listenClipboard } from "./paste";
import { isLink, picture } from "./picture";
import { nodeClipboard } from "./nodeClipboard";
import { answerSpots, spotIn } from "./spot";
import type { RendererKind } from "./renderer";
import { SelectionPill } from "./SelectionPill";
import { ZoomBar } from "./ZoomBar";

/** The graph window. */
export function GraphWindow(): JSX.Element {
  const { graph, view, actions, scene, sceneSync, wm, config, presence, nodeAdder, diagram, board, kinds, pages, assets, access, menu: contextMenu } = useApp();
  /** `true` while the class diagram or the blueprint is shown (the nodal view waits underneath). */
  const inDiagram = () => drawsBoxes(view.mode());
  /** `true` while the board is shown (the nodal view waits underneath too). */
  const onBoard = () => view.mode() === "board";
  /** `true` while a document is written (the nodal view waits underneath). */
  const onDocument = () => view.mode() === "document";
  /** Cancels the board's gesture in progress (set by the board view). */
  let cancelBoard: () => boolean = () => false;
  /** Cancels the diagram's gesture in progress (set by the diagram view). */
  let cancelDiagram: () => boolean = () => false;
  let host!: HTMLDivElement;
  let canvas!: HTMLCanvasElement;
  const [menu, setMenu] = createSignal<LinkMenuState | null>(null);
  const [cursor, setCursor] = createSignal("grab");
  /** Who paints the graph (the graphics card or the processor), once the canvas started. */
  const [renderer, setRenderer] = createSignal<RendererKind | null>(null);
  /** Whether the config asks for the graphics card (it does unless `graph { gpu #false }`). */
  const gpu = () => config.config()?.graph.gpu ?? true;
  const isEmpty = createMemo(() => graph.state.loaded && graph.state.graph.nodes.length === 0);
  const edits = { scene, actions, view, kinds };

  /** The selected node, as a scene object. */
  const selectedNode = () => {
    const s = view.state.selection;
    return s?.type === "node" ? (scene.byId.get(s.id) ?? null) : null;
  };
  /** The selected link, as a scene object. */
  const selectedLink = () => {
    const s = view.state.selection;
    return s?.type === "link" ? (scene.links.find((l) => isLink(l, s.link)) ?? null) : null;
  };
  /** The selected group, as a scene object. */
  const selectedGroup = () => {
    const s = view.state.selection;
    return s?.type === "group" ? (scene.groupById.get(s.id) ?? null) : null;
  };
  /** The selected bundle, as a scene object. */
  const selectedBundle = () => {
    const s = view.state.selection;
    return s?.type === "bundle" ? (scene.bundleByKey.get(s.key) ?? null) : null;
  };
  /** What the user is looking at: decides which bundles open into their links. */
  const focus = (): Focus => ({ node: pointer.hover() ?? selectedNode(), group: selectedGroup(), link: selectedLink(), bundle: selectedBundle() });
  /** Asks for a repaint. */
  const invalidate = () => (scene.redraw = true);

  const pointer = createPointer({
    scene,
    selectedNode,
    selectedLink,
    focus,
    linkTool: () => view.state.linkTool,
    setCursor,
    invalidate,
    // A folded group's disc stands for the group: clicking it selects the group.
    selectNode: (id) => {
      const folded = id ? foldedGroupOf(id) : null;
      if (folded) view.selectGroup(folded);
      else view.selectNode(id);
    },
    selectLink: view.selectLink,
    selectGroup: view.selectGroup,
    selectBundle: view.selectBundle,
    removeLink: (link) => void actions.removeLink(link).then((ok) => ok && toast("Link removed. Press Ctrl+Z to undo.")),
    linked: (from, to, at) => {
      if (from.fold || to.fold) return void toast("Unfold the group to link its nodes.");
      setMenu({ from, to, x: at.x, y: at.y });
    },
    linkedToNothing: (from, at) => {
      if (from.fold) return void toast("Unfold the group to link its nodes.");
      // The Add node window beside the drop, for a node there linked from `from`.
      const world = scene.toWorld(at.x, at.y);
      const r = canvas.parentElement!.getBoundingClientRect();
      nodeAdder.open({ x: world.x, y: world.y, z: world.z }, { x: r.left + at.x, y: r.top + at.y }, from.id);
    },
    reconnected: (r) => void reconnectLink(edits, r),
    dragged: sceneSync.dragged,
    nodeMoved: sceneSync.nodeMoved,
    pressed: () => setMenu(null),
  });

  /** The nodes' pictures, once loaded (the nodal view repaints when one arrives). */
  const pictures = createPictureCache(assets, () => (scene.redraw = true));
  // A picture's address arrives after the node is drawn: draw again.
  createEffect(() => {
    for (const n of graph.state.graph.nodes) {
      const path = pictureOf(n.attachments);
      if (path) assets.url(path);
    }
    scene.redraw = true;
  });

  onMount(() => {
    /** Set once the canvas started (the log below can be called while it starts). */
    let control: CanvasControl | undefined;
    control = startCanvas(host, canvas, scene, {
      images: pictures,
      picture: (palette) =>
        picture(scene, {
          palette,
          selectedNode: selectedNode(),
          selectedLink: selectedLink(),
          hoverNode: pointer.hover(),
          hoverLink: pointer.hoverLink(),
          selectedGroup: selectedGroup(),
          hoverGroup: pointer.hoverGroup(),
          lookAt: focus(),
          hoverBundle: pointer.hoverBundle(),
          query: view.state.query,
          labels: view.state.labels,
          linking: pointer.linking(),
          reconnecting: pointer.reconnecting(),
          hideHandles: pointer.draggingNode(),
          kinds: kinds.available(),
          mentions: pages.mentionLinks(),
        }),
      gpu: gpu(),
      log: (level, message) => {
        actions.log("graph", level, message);
        setRenderer(control?.renderer() ?? null);
      },
      settled: sceneSync.settled,
      coloursLate: (missing, ms) => actions.log("graph", "info", `the colours (${missing.length}) were read before the stylesheet applied; they arrived ${ms} ms later`),
    });
    const started = control;
    setRenderer(started.renderer());
    // The config changed its mind about the graphics card: switch painters, keep everything else.
    createEffect(() => {
      started.setGpu(gpu());
      setRenderer(started.renderer());
    });
    addEventListener("keydown", onKey, true);
    const stopSpots = answerSpots(spotDeps, host);
    const stopClipboard = listenClipboard({
      // The board has its own (`board/BoardView.tsx`); a document's text is its editor's.
      active: () => !onBoard() && !onDocument() && view.mode() !== null && access.can("editor"),
      copy: () => clipboard.copy(selectedNodeIds(view.state.selection)),
      remove: () => {
        const ids = selectedNodeIds(view.state.selection);
        if (ids.length) void actions.removeNodes(ids).then((ok) => ok && view.selectNode(null));
      },
      paste: (text) => void clipboard.paste(text, pointerAt),
    });
    onCleanup(() => {
      started.stop();
      removeEventListener("keydown", onKey, true);
      stopSpots();
      stopClipboard();
    });
  });

  /** What the spots of this window are worked out from. */
  const spotDeps = { view, scene, diagram, board };
  /** The last page point the pointer was at over this window (`null`: it left). */
  let pointerAt: { x: number; y: number } | null = null;

  /** Copy and paste of nodes in the nodal view and the diagram. */
  const clipboard = nodeClipboard({ graph, view, scene, diagram, board, actions, kinds }, () => host);

  /** Escape cancels what the graph window is in the middle of, before the app sees it. */
  function onKey(event: KeyboardEvent): void {
    if (event.key !== "Escape") return;
    const hadMenu = !!menu();
    setMenu(null);
    const cancelled = onBoard() ? cancelBoard() : inDiagram() ? cancelDiagram() : pointer.cancel();
    if (cancelled || hadMenu) event.stopImmediatePropagation();
  }

  /**
   * A pointer event's position relative to the canvas, measured on its host: the canvas itself
   * may be moved by a CSS transform while a paint is deferred (`canvas.ts`).
   */
  const point = (e: MouseEvent): Point => {
    const r = canvas.parentElement!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  /** Creates the link chosen in the menu. */
  const choose = (kind: string) => {
    const m = menu();
    setMenu(null);
    if (m) void createLink(edits, m.from, kind, m.to);
  };

  /** Zooms around the view's centre (the diagram's, while it is shown). */
  const zoom = (factor: number) => {
    if (onBoard()) {
      const r = canvas.parentElement!.getBoundingClientRect();
      return board.zoomAt(r.width / 2, r.height / 2, factor);
    }
    if (inDiagram()) return diagram.zoomAt(diagram.width / 2, diagram.height / 2, factor);
    scene.zoomAt(scene.width / 2, scene.height / 2, factor);
    invalidate();
  };

  /** What is under `p`, a folded group's disc counting as its group. */
  const targetAt = (p: Point): Target => {
    const target = pointer.targetAt(p);
    const folded = target.type === "node" ? target.node.fold?.group.id : undefined;
    const group = folded ? scene.groupById.get(folded) : undefined;
    return group ? { type: "group", group } : target;
  };

  /** Double click: a folded group's disc opens; a group's name is written in place. */
  const onDoubleClick = (e: MouseEvent) => {
    const p = point(e);
    const under = pointer.targetAt(p);
    if (under.type === "node" && under.node.fold) return void actions.updateGroup(under.node.fold.group.id, { collapsed: false });
    const named = scene.hitGroupLabel(p.x, p.y);
    if (named) view.renameGroup(named.id);
  };

  /** Right click: selects what is under the pointer and opens its menu. */
  const openMenu = (e: MouseEvent) => {
    setMenu(null);
    const target = targetAt(point(e));
    if (target.type === "node") view.selectNode(target.node.id);
    if (target.type === "link") view.selectLink(target.link.link);
    if (target.type === "group") view.selectGroup(target.group.id);
    if (target.type === "bundle") view.selectBundle(target.bundle.key);
    const at = point(e);
    const world = scene.toWorld(at.x, at.y);
    const near = { x: e.clientX, y: e.clientY };
    /** The Add node window beside the click, for a node where the click was. */
    const addNodeHere = () => nodeAdder.open({ x: world.x, y: world.y, z: world.z }, near);
    contextMenu.open(e, graphMenu({ graph: graph.state.graph, categories: graph.categories(), kinds, actions, view, wm, general: contextMenu.general, zoom, addNodeHere, clipboard: clipboard.menu(near) }, target));
  };

  /** Files of the computer dropped here: kept as the project's assets, then a node attached to them (not when a view took them, such as a document its pictures). */
  const dropFiles = async (e: DragEvent) => {
    const files = [...(e.dataTransfer?.files ?? [])];
    if (!files.length || e.defaultPrevented) return;
    e.preventDefault();
    if (!access.can("editor")) return void toast("Editors and owners add files to the project.", "error");
    const spot = spotIn(spotDeps, host, e.clientX, e.clientY);
    const kept = await assets.upload("project", files, "");
    if (!kept.length) return;
    nodeAdder.open(spot.hint, { x: e.clientX, y: e.clientY }, null, assetStem(kept[0]!.path));
    nodeAdder.attach(kept.map((k) => k.path));
    nodeAdder.placeAt(spot);
  };

  return (
    <div
      class="stage"
      ref={host}
      data-renderer={renderer() ?? undefined}
      onDragOver={(e) => e.dataTransfer?.types.includes("Files") && e.preventDefault()}
      onDrop={(e) => void dropFiles(e)}
      onPointerMove={(e) => (pointerAt = { x: e.clientX, y: e.clientY })}
      onPointerLeave={() => (pointerAt = null)}
    >
      <canvas
        ref={canvas}
        class="graph-canvas"
        style={{ cursor: cursor(), visibility: inDiagram() || onBoard() || onDocument() ? "hidden" : undefined }}
        aria-label="Architecture graph. Use the search box and the inspector to browse nodes with the keyboard."
        onPointerDown={(e) => {
          canvas.setPointerCapture?.(e.pointerId);
          pointer.down(e, point(e));
        }}
        onPointerMove={(e) => {
          const p = point(e);
          pointer.move(e, p);
          // Attached to the node under (or nearest to) the pointer, with the line of sight in 3D.
          presence.moveCursor(pointerCursor(scene, p.x, p.y));
        }}
        onPointerUp={(e) => pointer.up(e, point(e))}
        onPointerCancel={(e) => pointer.up(e, point(e))}
        onPointerLeave={() => {
          pointer.leave();
          presence.moveCursor(null);
        }}
        onWheel={(e) => pointer.wheel(e, point(e))}
        onContextMenu={openMenu}
        onDblClick={onDoubleClick}
      />
      <Show when={onBoard()}>
        <BoardView provideCancel={(cancel) => (cancelBoard = cancel)} />
      </Show>
      <Show when={onDocument()}>
        <DocumentView />
      </Show>
      <Show when={inDiagram()} fallback={<Show when={!onBoard() && !onDocument()}><PeopleLayer /></Show>}>
        <DiagramView
          blueprint={view.mode() === "blueprint"}
          linked={(from, to, at) => setMenu({ from, to, x: at.x, y: at.y })}
          pressed={() => setMenu(null)}
          provideCancel={(cancel) => (cancelDiagram = cancel)}
          zoom={zoom}
        />
      </Show>
      <Show when={!onBoard() && !onDocument()}>
        <SelectionPill />
      </Show>
      <ZoomBar zoom={zoom} />
      <Show when={graph.state.loaded && view.mode() === null}>
        <div class="empty-graph no-view" id="graph-no-view">
          <p>No view of the graph is on here.</p>
          <p class="muted">Views are plugins: turn on one (the nodal view, the class diagram) or an environment that uses them, such as Coding.</p>
          <button type="button" class="btn primary" onClick={() => wm.open("plugins")}>
            Choose plugins
          </button>
        </div>
      </Show>
      <Show when={isEmpty() && view.mode() !== null && !onBoard() && !onDocument()}>
        <div class="empty-graph">
          <p>The graph is empty.</p>
          <p class="muted">Right-click to add a node (or press N).</p>
        </div>
      </Show>
      <Show when={!graph.state.loaded}>
        <div class="empty-graph muted">Waiting for the core…</div>
      </Show>
      <div class="hint" hidden={onDocument()}>
        <Show
          when={!inDiagram() && !onBoard()}
          fallback={
            onBoard()
              ? "Board: pick a tool on the left or double-click for a note; drag to move, the handles resize and turn, Shift+drag selects, Ctrl+G groups, Ctrl+D duplicates, Space+drag or the middle button pans."
              : view.mode() === "blueprint"
              ? "Blueprint: + on a box adds a pin (a choice); drag a pin onto a box to wire it, or onto empty space to add the node it leads to. Right-click a pin to rename or remove it. Press D for the next view."
              : "Class diagram: drag a box or a package to move it, Shift+drag to select boxes (Ctrl+click adds one), drag from a box's handle to link it, click a count to list the links between two packages, Alt+click an arrow to delete it. Press D for the next view."
          }
        >
          <Show when={view.state.dimension === "3d"} fallback="Drag from the handle beside a node to link it. Click a link to edit it. Alt+click a link to delete it. Drag a group by its name to move it, double-click its name to rename it, C folds it.">
            3D view: drag the background to turn the graph, Ctrl+drag or the middle button to pan, the wheel to zoom. Press 3 to go back to 2D.
          </Show>
        </Show>
      </div>
      <Show when={menu()}>{(m) => <LinkMenu menu={m()} kinds={kinds.links()} suggested={kinds.suggest(m().from.node.kind, m().to.node.kind)} width={scene.width} height={scene.height} onChoose={choose} />}</Show>
    </div>
  );
}
